import { Link } from 'react-router-dom';
import { RecordsTable, type Column, type FilterDef } from '../components/RecordsTable/RecordsTable';
import { DeleteRowButton } from '../components/DeleteRowButton/DeleteRowButton';
import { money, pct } from '../lib/format';
import './Reps.css';

interface RepRow {
  id: number;
  public_id: string;
  email: string;
  display_name: string;
  territory: string | null;
  commission_rate: number | string | null; // MCP returns numeric as a STRING (fraction, e.g. "0.0500")
  status: 'active' | 'inactive' | 'suspended';
  // Lifetime earned commission (SUM of rep_commissions.commission_earned_usd, excluding voided),
  // returned by listReps as a numeric STRING. COALESCEd to 0 server-side, so a rep with no
  // commissions reads "$0.00" — real and rankable, not fabricated. Reads 0 across the board until
  // rep commissions are actually written (rep-portal/CRM track).
  commission_total_usd?: number | string | null;
}

const FILTERS: FilterDef<RepRow>[] = [
  {
    key: 'status',
    label: 'Status',
    options: [
      { value: 'all', label: 'All' },
      { value: 'active', label: 'Active' },
      { value: 'inactive', label: 'Inactive' },
      { value: 'suspended', label: 'Suspended' },
    ],
    match: (r, v) => r.status === v,
  },
];

export function Reps() {
  const columns: Column<RepRow>[] = [
    { key: 'display_name', header: 'Name', sortable: true, render: (r) => <Link className="records__link" to={`/reps/${r.id}`}>{r.display_name || '—'}</Link> },
    { key: 'email', header: 'Email', render: (r) => r.email || '—' },
    { key: 'territory', header: 'Territory', render: (r) => r.territory || '—' },
    { key: 'commission_rate', header: 'Commission', numeric: true, sortable: true, sortAccessor: (r) => Number(r.commission_rate), render: (r) => pct(r.commission_rate) },
    // Total earned commission per rep — the revenue ranking. Sortable; the page defaults to a
    // descending sort so it opens highest→lowest grossing. Click the header to flip lowest→highest.
    { key: 'commission_total_usd', header: 'Total Commission', numeric: true, sortable: true, sortAccessor: (r) => (r.commission_total_usd == null ? null : Number(r.commission_total_usd)), render: (r) => money(r.commission_total_usd) },
    {
      key: 'status',
      header: 'Status',
      render: (r) => (
        <span className={`records__tag records__tag--${r.status === 'active' ? 'ok' : 'warn'}`}>{r.status}</span>
      ),
    },
  ];

  return (
    <RecordsTable<RepRow>
      title="Reps"
      intro="The sales team that recruits HVAC contractors into Cabana dealers. Each rep shows their territory, commission rate, total earned commission, and status — sorted highest-to-lowest grossing by default (click any sortable header to re-rank). Click a name to open their full profile."
      tool="listReps"
      args={{ limit: 200 }}
      select={(r) => (r as { reps: RepRow[] }).reps ?? []}
      columns={columns}
      rowKey={(r) => r.id}
      emptyMessage="No reps yet."
      refreshable
      searchable={{
        placeholder: 'Search name, email, or territory…',
        match: (r, q) =>
          [r.display_name, r.email, r.territory].some((f) => f != null && f.toLowerCase().includes(q)),
      }}
      filters={FILTERS}
      defaultSort={{ key: 'commission_total_usd', dir: 'desc' }}
      rowClassName={(r) => (r.status !== 'active' ? 'records__row--inactive' : undefined)}
      /* Backlog 1033. Seven tables reference `reps` and NONE of them cascades, so any rep
         who has been assigned a dealer, earned a commission, touched a lead or appeared in
         a lead audit event is refused with the list. In practice that means this only ever
         removes a duplicate row, which is what it is for.

         Note reps have no deactivate tool the way dealers do, so a real rep who leaves has
         no lifecycle action at all today. That is a gap, and it is not one this button
         should be bent into filling. */
      actions={{
        header: '',
        render: (r) => (
          <DeleteRowButton
            row={r}
            action="deleteRep"
            noun="rep"
            label={r.display_name}
            invalidate={['mcp', 'listReps']}
          />
        ),
      }}
    />
  );
}
